/**
 * Route Guards
 *
 * Wrapper components that protect routes based on authentication and authorization state.
 *
 * - AuthGuard: Protects routes requiring authentication
 * - AdminOrStaffGuard: Protects routes requiring member-management reach
 *   (active-org admin, staff admin, or live ZITADEL member-management authority)
 *
 * Guards are designed to be composed:
 * - Landing page: no guard (public)
 * - Users page: AuthGuard wrapping AdminOrStaffGuard wrapping the page content
 * - No-access page: AuthGuard only (authenticated, but no member-management reach)
 * - Error page: no guard (accessible in any state)
 *
 * @module routes/guards
 */

import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useAuthStore } from '../stores/useAuthStore';

// =============================================================================
// Types
// =============================================================================

export interface GuardProps {
  children: React.ReactNode;
}

// =============================================================================
// Loading Indicator
// =============================================================================

/**
 * Centered loading indicator shown during auth state initialization.
 */
function LoadingIndicator() {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
      }}
    >
      <CircularProgress />
    </Box>
  );
}

// =============================================================================
// AuthGuard
// =============================================================================

/**
 * AuthGuard - Protects routes requiring authentication.
 *
 * Behavior:
 * - While isInitialized is false (boot sequence in progress), renders a loading indicator
 * - Once initialized, if not authenticated, redirects to the landing page
 * - If authenticated, renders children
 *
 * Deep Link Preservation:
 * - When redirecting an unauthenticated user, preserves the intended destination
 * - The current path is passed as a `returnTo` state parameter to the landing page
 * - After login, the user returns to where they were trying to go
 *
 * @example
 * ```tsx
 * <Route
 *   path="/users"
 *   element={
 *     <AuthGuard>
 *       <AdminOrStaffGuard>
 *         <UsersPage />
 *       </AdminOrStaffGuard>
 *     </AuthGuard>
 *   }
 * />
 * ```
 */
export function AuthGuard({ children }: GuardProps) {
  const { isInitialized, isAuthenticated } = useAuthStore();
  const location = useLocation();

  // Show loading indicator while auth state is initializing
  if (!isInitialized) {
    return <LoadingIndicator />;
  }

  // Redirect to landing page if not authenticated
  // Preserve the intended destination for deep link return
  if (!isAuthenticated) {
    return (
      <Navigate
        to="/"
        replace
        state={{ returnTo: location.pathname + location.search + location.hash }}
      />
    );
  }

  // User is authenticated, render children
  return <>{children}</>;
}

// =============================================================================
// AdminOrStaffGuard
// =============================================================================

/**
 * AdminOrStaffGuard - Protects routes that an active-org admin **or** a
 * platform staff admin may access.
 *
 * Behavior:
 * - Assumes AuthGuard has already verified authentication
 * - Reads isUserAdmin and isStaffAdmin from the auth store
 * - If the user is an active-org admin OR a staff admin, renders children
 * - Otherwise redirects to the no-access page
 *
 * Why all three: a **staff admin** manages other orgs' members via the org
 * picker, and their staff grant typically lives in the vendor org — which usually
 * is NOT the login-context active org. And a **vendor admin** may hold real
 * ZITADEL user-management authority (an instance `IAM_*` membership) without the
 * `platform.user.admin` project role, so `isUserAdmin` is false for them too.
 * Gating on `isUserAdmin` alone would wrongly bounce both. This guard admits
 * anyone who is an active-org admin, a staff admin, OR can manage members in their
 * home org ({@link useAuthStore.canManageMembers}, resolved from live ZITADEL
 * authority) — matching the invite-affordance gate.
 *
 * No loading state is needed — AuthGuard handles that.
 *
 * @example
 * ```tsx
 * <AuthGuard>
 *   <AdminOrStaffGuard>
 *     <UsersPage />
 *   </AdminOrStaffGuard>
 * </AuthGuard>
 * ```
 */
export function AdminOrStaffGuard({ children }: GuardProps) {
  const { isUserAdmin, isStaffAdmin, canManageMembers } = useAuthStore();

  // Redirect to the no-access page unless the caller is an active-org admin, a
  // staff admin, or holds live member-management authority in their home org.
  if (!isUserAdmin && !isStaffAdmin && !canManageMembers) {
    return <Navigate to="/no-access" replace />;
  }

  // User may access the page, render children
  return <>{children}</>;
}
